import React, { useState, useEffect } from "react";
import QuillEditor from "../components/QuillEditor";
import DOMPurify from "dompurify";
import api from "../api/axios";
import DoctorNavbar from "../components/DoctorNavbar";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "bullet" }, { list: "ordered" }],
    ["link", "image", "clean"],
  ],
};

function DoctorCreatePost() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [shortContent, setShortContent] = useState("");
  const [largeContent, setLargeContent] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [doctorId, setDoctorId] = useState(null);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const res = await api.get('doctor/session-check/');
      if (res.data.authenticated) {
        setDoctorId(res.data.doctor_id);
      } else {
        navigate('/doctor/login');
      }
    } catch {
      navigate('/doctor/login');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.warning("Image size should be less than 5MB", {
          position: "bottom-right",
          autoClose: 3000,
        });
        return;
      }
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const submitPost = async () => {
    if (!title.trim()) {
      toast.warning("Please enter a title for your post.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    if (!largeContent.trim()) {
      toast.warning("Please write some content before posting.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    if (largeContent === '<p><br></p>') {
      toast.warning("Please write some content before posting.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('doctor_id', doctorId);
      formData.append('title', title);
      formData.append('short_content', ''); // Empty short content
      formData.append('large_content', DOMPurify.sanitize(largeContent));
      
      if (image) {
        formData.append('image', image);
      }

      await api.post("social/posts/", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success("Post created successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
      navigate('/doctor/posts');
    } catch (err) {
      console.error("Error creating post:", err);
      toast.error(err.response?.data?.error || "Failed to create post. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100">
      <DoctorNavbar />
      
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            
            <div className="d-flex align-items-center mb-4">
              <button className="btn btn-link text-decoration-none text-muted p-0 me-3" onClick={() => navigate(-1)}>
                <i className="bi bi-arrow-left fs-4"></i>
              </button>
              <h2 className="fw-bold mb-0">Create Health Post</h2>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                
                {/* TITLE FIELD */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-primary">
                    <i className="bi bi-type me-2"></i>
                    Post Title
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="Enter a catchy title for your post..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={255}
                  />
                  <small className="text-muted">{title.length}/255 characters</small>
                </div>

                {/* LARGE CONTENT */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-primary">
                    <i className="bi bi-file-text me-2"></i>
                    Full Content
                  </label>
                  <p className="text-muted small mb-2">Write the complete content for your post. This will be shown in the feed cards and when users click "Read More".</p>
                  <QuillEditor
                    value={largeContent}
                    onChange={setLargeContent}
                    modules={modules}
                    placeholder="Write the full content here..."
                    style={{ height: '300px', marginBottom: '50px' }}
                  />
                </div>

                {/* IMAGE UPLOAD */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-primary">
                    <i className="bi bi-image me-2"></i>
                    Post Image (Optional)
                  </label>
                  <div className="d-flex gap-3 align-items-start">
                    <div className="flex-grow-1">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      <small className="text-muted">Max size: 5MB</small>
                    </div>
                    {/* {imagePreview && (
                      <div className="position-relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="rounded shadow-sm"
                          style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                          onClick={removeImage}
                          style={{ width: '30px', height: '30px', padding: 0 }}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    )} */}
                  </div>
                </div>

                {/* SUBMIT BUTTON */}
                <div className="d-grid pt-3">
                  <button 
                    className="btn btn-primary btn-lg fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center"
                    onClick={submitPost}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Publishing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        Publish Post
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-info bg-opacity-10 border border-info border-opacity-25 rounded-4">
              <div className="d-flex">
                <i className="bi bi-info-circle-fill text-info fs-4 me-3"></i>
                <div>
                  <h6 className="fw-bold text-info mb-1">Post Guidelines</h6>
                  <ul className="small text-muted mb-0">
                    <li>Your posts will be visible to all registered users on FitNova.</li>
                    <li>Ensure your advice is accurate, professional, and helpful.</li>
                    <li>Your content will appear in feed cards and can be viewed in full when users click "Read More".</li>
                    <li>You can edit or delete your posts later from the posts page.</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default DoctorCreatePost;
